/** Time frame for closed positions: presets plus a custom date range, with the totals both desktop and phone show. */
import { D, rangeBounds, type Position } from '@platform/shared';
import { isCustom, periodText, TimeFrameSelect, type Period as Choice } from './DateRange';

export type PeriodId = 'all' | 'today' | '3d' | '7d' | '30d';
/** A preset or a custom range of the viewer's calendar days. */
export type Period = Choice<PeriodId>;

const PRESETS: { id: PeriodId; label: string; days?: number }[] = [
  { id: 'today', label: 'Today' },
  { id: '3d', label: '3 days', days: 3 },
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: 'all', label: 'All' },
];

export const DEFAULT_PERIOD: Period = { id: '7d' };

/** [from, to) in epoch ms. */
export function periodRange(p: Period): [number, number] {
  if (isCustom(p)) return rangeBounds(p.range);
  if (p.id === 'all') return [0, Infinity];
  if (p.id === 'today') {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return [d.getTime(), Infinity];
  }
  return [Date.now() - (PRESETS.find((x) => x.id === p.id)?.days ?? 0) * 86_400_000, Infinity];
}

export function periodLabel(p: Period): string {
  return periodText(p, PRESETS) || 'All';
}

export function inPeriod(rows: Position[], p: Period): Position[] {
  const [from, to] = periodRange(p);
  return rows.filter((r) => {
    if (!r.closedAt) return p.id === 'all';
    const t = new Date(r.closedAt).getTime();
    return t >= from && t < to;
  });
}

/** Fees + swap (negative = cost) and net PnL after fees for a closed position. */
export function closedCosts(p: Position) {
  const fees = D.from(p.openFee).add(p.closeFee ?? '0');
  const realized = p.realizedPnl ? D.from(p.realizedPnl) : null;
  return { fees, realized, feesSwap: D.from(p.swap).sub(fees), net: realized ? realized.sub(fees) : null };
}

export function closedTotals(rows: Position[]) {
  let net = D.ZERO;
  let fees = D.ZERO;
  let swap = D.ZERO;
  let wins = 0;
  for (const p of rows) {
    const c = closedCosts(p);
    fees = fees.add(c.fees);
    swap = swap.add(p.swap);
    if (c.net) {
      net = net.add(c.net);
      if (c.net.isPos()) wins++;
    }
  }
  return { count: rows.length, net, fees, swap, feesSwap: swap.sub(fees), winRate: rows.length ? `${Math.round((wins / rows.length) * 100)}%` : '—' };
}

/** Desktop dropdown with a check on the selected option and a “Custom range…” calendar. */
export function PeriodSelect({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  return <TimeFrameSelect presets={PRESETS} value={value} onChange={onChange} />;
}
